/**
 * The owner's third question — which invoices were never collected — is a screen, not a
 * filter. It is the default view here, and it opens on the money he is owed rather than on a
 * neutral list he has to think to narrow.
 */

import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { formatIQD } from "@fanni/shared/money";
import {
  INVOICE_STATUS_AR,
  PAYMENT_METHOD_AR,
  daysOverdue,
  fmtDate,
  fmtDateTime,
  isOverdue,
} from "../lib/format.js";
import { useInvoice, useInvoices, useRecordPayment } from "../lib/queries.js";
import type { InvoiceListRow, InvoiceStatus } from "../lib/types.js";
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Field,
  Input,
  Loading,
  Modal,
  Note,
  PageHeader,
  Select,
  Stat,
  Table,
  Td,
  Th,
  Tr,
  cx,
} from "../ui/kit.js";
import type { Tone } from "../ui/kit.js";

const STATUS_TONE: Record<InvoiceStatus, Tone> = {
  draft: "neutral",
  issued: "warn",
  partial: "warn",
  paid: "good",
  void: "neutral",
};

type View = "overdue" | "uncollected" | "all";

const VIEW_AR: Record<View, string> = {
  overdue: "متأخرة",
  uncollected: "لم تُحصَّل",
  all: "الكل",
};

const isView = (v: string | null): v is View => v === "overdue" || v === "uncollected" || v === "all";

export function Invoices() {
  const [params, setParams] = useSearchParams();
  const raw = params.get("view");
  const view: View = isView(raw) ? raw : "uncollected";
  const [payFor, setPayFor] = useState<InvoiceListRow | null>(null);

  const { data, isLoading, error } = useInvoices();

  /**
   * Two different sets, and the distinction is the product.
   *
   * `uncollected` — issued or partly paid and still owed. Draft was never sent; void was
   * cancelled. Some of this is perfectly healthy: an invoice issued yesterday on net-14 is
   * not a problem.
   * `overdue` — the subset that blew the date the customer agreed to. This is the owner's
   * third question, and it is a strictly smaller and more serious claim.
   */
  const uncollected = useMemo(
    () =>
      (data?.invoices ?? [])
        .filter((i) => i.outstanding_fils > 0 && (i.status === "issued" || i.status === "partial"))
        // Latest overdue first; invoices with no terms sink to the bottom rather than
        // masquerading as on-time.
        .sort((a, b) => (daysOverdue(b.dueAt) ?? -Infinity) - (daysOverdue(a.dueAt) ?? -Infinity)),
    [data],
  );

  const overdue = useMemo(() => uncollected.filter((i) => isOverdue(i)), [uncollected]);

  const rows =
    view === "overdue" ? overdue : view === "uncollected" ? uncollected : (data?.invoices ?? []);

  const totalOutstanding = uncollected.reduce((a, i) => a + i.outstanding_fils, 0);
  const overdueFils = overdue.reduce((a, i) => a + i.outstanding_fils, 0);
  const worst = overdue.length > 0 ? daysOverdue(overdue[0]!.dueAt) : null;
  const noTerms = uncollected.filter((i) => i.dueAt === null).length;

  return (
    <>
      <PageHeader
        title="الفواتير"
        subtitle="ما صدر ولم يُحصَّل — السؤال الثالث الذي لا يستطيع المالك الإجابة عنه اليوم."
        actions={
          <div className="flex gap-1 rounded-md border border-line p-0.5">
            {(["overdue", "uncollected", "all"] as View[]).map((v) => (
              <button
                key={v}
                onClick={() => {
                  const next = new URLSearchParams(params);
                  next.set("view", v);
                  setParams(next, { replace: true });
                }}
                className={cx(
                  "rounded px-3 py-1 text-xs font-medium transition",
                  view === v ? "bg-accent text-white" : "text-ink-soft hover:bg-line-soft",
                )}
              >
                {VIEW_AR[v]}
                {v === "overdue" && overdue.length > 0 ? (
                  <span className="num"> ({overdue.length})</span>
                ) : null}
              </button>
            ))}
          </div>
        }
      />

      {error ? <ErrorNote error={error} className="mb-4" /> : null}

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {/*
          "مستحقة" and "متأخرة" are two claims, not one. The first is money owed; the second is
          money owed LATE. They are shown side by side and never summed into a single scary
          number, because the owner acts on them differently.
        */}
        <Stat
          label="لم يُحصَّل (المستحق كله)"
          value={<span className="num">{formatIQD(totalOutstanding)}</span>}
          hint={`${uncollected.length} فاتورة — متأخرة كانت أو ضمن موعدها`}
          tone={totalOutstanding > 0 ? "warn" : "good"}
        />
        <Stat
          label="متأخرة عن موعدها"
          value={<span className="num">{formatIQD(overdueFils)}</span>}
          hint={`${overdue.length} فاتورة تجاوزت تاريخ استحقاقها`}
          tone={overdueFils > 0 ? "bad" : "good"}
        />
        <Stat
          label="أقدم تأخير"
          value={<span className="num-rtl">{worst === null ? "—" : `${worst} يوم`}</span>}
          hint={worst === null ? "لا توجد فاتورة متأخرة" : undefined}
          tone={worst !== null && worst > 0 ? "bad" : "neutral"}
        />
        <Stat
          label="إجمالي الفواتير"
          value={<span className="num">{data?.invoices.length ?? 0}</span>}
          tone="neutral"
        />
      </div>

      {/*
        The one thing this screen still cannot answer honestly. An invoice with no `due_at` has
        no agreed deadline, so it can be owed but cannot be LATE. It is counted in the
        outstanding total and excluded from overdue — and rather than let that silently shrink
        the overdue figure, the screen says how many there are.
      */}
      {noTerms > 0 ? (
        <Note className="mb-3">
          <span className="num">{noTerms}</span> من الفواتير غير المحصّلة بلا تاريخ استحقاق — تُحتسب
          ضمن «المستحق» ولا تُحتسب ضمن «المتأخرة»، لأن ما لا موعد له لا يمكن أن يتأخر. حدِّد شروط
          الدفع عند الإصدار ليظهر تأخيرها هنا.
        </Note>
      ) : null}

      <Card bodyClassName="">
        {isLoading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>
            {view === "overdue"
              ? uncollected.length > 0
                ? "لا توجد فاتورة تجاوزت موعدها. يبقى مستحق لم يُحصَّل بعد لكنه ضمن موعده — راجع «لم تُحصَّل»."
                : "لا توجد فاتورة متأخرة."
              : view === "uncollected"
                ? "كل الفواتير محصّلة. لا شيء معلّق."
                : "لا توجد فواتير."}
          </Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>الرقم</Th>
                <Th>الزبون</Th>
                <Th>الحالة</Th>
                <Th>المجموع</Th>
                <Th>المدفوع</Th>
                <Th>المتبقي</Th>
                <Th>صدرت</Th>
                <Th>تستحق</Th>
                <Th>التأخير</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {rows.map((i) => {
                const late = isOverdue(i);
                const by = daysOverdue(i.dueAt);
                return (
                  <Tr key={i.id} className={late ? "bg-bad-soft/40" : undefined}>
                    <Td>
                      <Link to={`/invoices/${i.id}`} className="num font-medium text-accent hover:underline">
                        {i.number ?? "—"}
                      </Link>
                    </Td>
                    <Td className="font-medium">{i.customerName ?? "—"}</Td>
                    <Td>
                      <Badge tone={STATUS_TONE[i.status]}>{INVOICE_STATUS_AR[i.status]}</Badge>
                    </Td>
                    <Td>
                      <span className="num">{formatIQD(i.totalFils)}</span>
                    </Td>
                    <Td>
                      <span className="num text-ink-soft">{formatIQD(i.paidFils)}</span>
                    </Td>
                    <Td>
                      <span className={cx("num font-bold", i.outstanding_fils > 0 ? "text-warn" : "text-good")}>
                        {formatIQD(i.outstanding_fils)}
                      </span>
                    </Td>
                    <Td>
                      <span className="num text-ink-soft">{fmtDate(i.issuedAt)}</span>
                    </Td>
                    <Td>
                      <span className="num text-ink-soft">{fmtDate(i.dueAt)}</span>
                    </Td>
                    {/*
                      Only ever says "late by N" when it IS late. An invoice inside its terms
                      shows how long it still has; one with no terms shows "—" and claims
                      nothing at all.
                    */}
                    <Td>
                      {i.dueAt === null ? (
                        <span className="text-ink-faint">بلا موعد</span>
                      ) : late ? (
                        <span className="num-rtl font-bold text-bad">متأخرة {by} يوم</span>
                      ) : i.outstanding_fils > 0 ? (
                        <span className="num-rtl text-ink-soft">باقٍ {Math.abs(by ?? 0)} يوم</span>
                      ) : (
                        <span className="text-ink-faint">—</span>
                      )}
                    </Td>
                    <Td>
                      {i.outstanding_fils > 0 && i.status !== "void" ? (
                        <Button size="sm" variant="primary" onClick={() => setPayFor(i)}>
                          تسجيل دفعة
                        </Button>
                      ) : null}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      {payFor ? <PaymentModal invoice={payFor} onClose={() => setPayFor(null)} /> : null}
    </>
  );
}

/**
 * Money movement is never client-authoritative (SYNC.md Rule 4). The console posts an amount
 * and the server decides what the invoice's status becomes and whether the job closes.
 */
export function PaymentModal({
  invoice,
  onClose,
}: {
  invoice: { id: string; number: string | null; outstanding_fils: number; customerName?: string | null };
  onClose: () => void;
}) {
  const record = useRecordPayment();
  const [amount, setAmount] = useState(String(invoice.outstanding_fils));
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");

  const amountFils = Math.round(Number(amount) || 0);
  const valid = amountFils > 0;

  return (
    <Modal title={`تسجيل دفعة — فاتورة ${invoice.number ?? ""}`} onClose={onClose}>
      {record.error ? <ErrorNote error={record.error} className="mb-3" /> : null}

      <Note className="mb-3">
        المتبقي على الفاتورة:{" "}
        <span className="num font-bold">{formatIQD(invoice.outstanding_fils)}</span>
      </Note>

      <div className="space-y-3">
        <Field label="المبلغ (فلس)" hint={`= ${formatIQD(amountFils)}`}>
          <Input type="number" min={1} dir="ltr" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="الطريقة">
          <Select value={method} onChange={(e) => setMethod(e.target.value)}>
            {Object.entries(PAYMENT_METHOD_AR).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="المرجع (اختياري)" hint="رقم الصك أو الحوالة">
          <Input value={reference} onChange={(e) => setReference(e.target.value)} />
        </Field>
      </div>

      <Button
        variant="primary"
        className="mt-4 w-full"
        disabled={record.isPending || !valid}
        onClick={() =>
          record.mutate(
            {
              invoice_id: invoice.id,
              amount_fils: amountFils,
              method: method as "cash" | "transfer" | "cheque",
              reference: reference.trim() || undefined,
            },
            { onSuccess: onClose },
          )
        }
      >
        تسجيل
      </Button>

      {amountFils > invoice.outstanding_fils ? (
        <p className="mt-2 text-xs text-warn">
          المبلغ أكبر من المتبقي. سيُسجَّل كما هو وتصبح الفاتورة مدفوعة.
        </p>
      ) : null}
    </Modal>
  );
}

// ── detail ───────────────────────────────────────────────────────────────────

export function InvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useInvoice(id);
  const [paying, setPaying] = useState(false);

  if (isLoading) return <Loading />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  const inv = data.invoice;
  const outstanding = inv.totalFils - inv.paidFils;
  const late = isOverdue({ dueAt: inv.dueAt, outstanding_fils: outstanding, status: inv.status });
  const by = daysOverdue(inv.dueAt);

  return (
    <>
      <PageHeader
        title={`فاتورة ${inv.number ?? ""}`}
        subtitle={
          <span className="flex items-center gap-2">
            <Badge tone={STATUS_TONE[inv.status]}>{INVOICE_STATUS_AR[inv.status]}</Badge>
            {late ? <Badge tone="bad">متأخرة {by} يوم</Badge> : null}
            <span className="num text-ink-faint">صدرت {fmtDate(inv.issuedAt)}</span>
            <span className="num text-ink-faint">
              تستحق {inv.dueAt ? fmtDate(inv.dueAt) : "— بلا موعد"}
            </span>
            {data.customer ? (
              <Link to={`/customers/${data.customer.id}`} className="text-accent hover:underline">
                {data.customer.name}
              </Link>
            ) : null}
          </span>
        }
        actions={
          <>
            {inv.jobId ? (
              <Link
                to={`/jobs/${inv.jobId}`}
                className="text-xs font-medium text-accent hover:underline"
              >
                فتح المهمة
              </Link>
            ) : null}
            {outstanding > 0 && inv.status !== "void" ? (
              <Button variant="primary" size="sm" onClick={() => setPaying(true)}>
                تسجيل دفعة
              </Button>
            ) : null}
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="البنود" className="lg:col-span-2" bodyClassName="">
          <Table>
            <thead>
              <tr>
                <Th>الوصف</Th>
                <Th>الكمية</Th>
                <Th>سعر الوحدة</Th>
                <Th>المجموع</Th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map((l) => (
                <Tr key={l.id}>
                  <Td>{l.description}</Td>
                  <Td>
                    <span className="num">{l.qty}</span>
                  </Td>
                  <Td>
                    <span className="num">{formatIQD(l.unitPriceFils)}</span>
                  </Td>
                  <Td>
                    <span className="num font-medium">{l.line_total_display}</span>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>

          <dl className="space-y-1.5 border-t border-line p-4 text-sm">
            <Line label="المجموع الفرعي" value={formatIQD(inv.subtotalFils)} />
            {inv.discountFils > 0 ? <Line label="الخصم" value={`- ${formatIQD(inv.discountFils)}`} /> : null}
            {inv.taxFils > 0 ? <Line label="الضريبة" value={formatIQD(inv.taxFils)} /> : null}
            <Line label="الإجمالي" value={inv.total_display} bold />
            <Line label="المدفوع" value={formatIQD(inv.paidFils)} />
            <Line label="المتبقي" value={formatIQD(outstanding)} bold tone={outstanding > 0 ? "warn" : "good"} />
          </dl>
        </Card>

        <Card title="الدفعات" bodyClassName="">
          {data.payments.length === 0 ? (
            <Empty>لم تُسجَّل دفعات.</Empty>
          ) : (
            <ul className="divide-y divide-line-soft">
              {data.payments.map((p) => (
                <li key={p.id} className="px-4 py-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="num font-bold text-good">{formatIQD(p.amountFils)}</span>
                    <Badge tone="neutral">{PAYMENT_METHOD_AR[p.method]}</Badge>
                  </div>
                  <p className="num mt-0.5 text-xs text-ink-faint">{fmtDateTime(p.receivedAt)}</p>
                  {p.reference ? <p className="num text-xs text-ink-soft">{p.reference}</p> : null}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {paying ? (
        <PaymentModal
          invoice={{ id: inv.id, number: inv.number, outstanding_fils: outstanding }}
          onClose={() => setPaying(false)}
        />
      ) : null}
    </>
  );
}

function Line({
  label,
  value,
  bold,
  tone,
}: {
  label: string;
  value: string;
  bold?: boolean;
  tone?: "warn" | "good";
}) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-soft">{label}</dt>
      <dd
        className={cx(
          "num",
          bold && "font-bold",
          tone === "warn" && "text-warn",
          tone === "good" && "text-good",
        )}
      >
        {value}
      </dd>
    </div>
  );
}
